import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  addDays,
  subDays,
  startOfDay,
  isSameDay,
  isTomorrow,
  format,
} from 'date-fns';
import { getNowInTz } from '../lib/timezone';

/** Check if a date is a school day (Mon-Fri) */
function isSchoolDay(d: Date): boolean {
  const day = d.getDay();
  return day >= 1 && day <= 5;
}

/** Get the next school day from a given date */
function getNextSchoolDay(from: Date): Date {
  let d = addDays(from, 1);
  while (!isSchoolDay(d)) {
    d = addDays(d, 1);
  }
  return d;
}

/** Get the previous school day from a given date */
function getPrevSchoolDay(from: Date): Date {
  let d = subDays(from, 1);
  while (!isSchoolDay(d)) {
    d = subDays(d, 1);
  }
  return d;
}

interface UseSchoolDateReturn {
  schoolDate: Date;
  schoolDateLabel: string;
  schoolDateSubtitle: string;
  handleSchoolPrev: () => void;
  handleSchoolNext: () => void;
  handleSchoolReset: () => void;
}

export function useSchoolDate(
  familyTimezone: string,
  viewMode: string,
): UseSchoolDateReturn {
  // Compute the smart default school date
  const getSmartSchoolDate = useCallback(() => {
    const nowTz = getNowInTz(familyTimezone);
    const hour = nowTz.getHours();
    const today = startOfDay(nowTz);

    if (isSchoolDay(today) && hour < 16) {
      // Before 4pm on a school day: show today
      return today;
    }
    // After 4pm or weekend: show next school day (prep for tomorrow)
    return getNextSchoolDay(today);
  }, [familyTimezone]);

  // School date state - separate from the general calendar date
  const [schoolDate, setSchoolDate] = useState<Date>(() => getSmartSchoolDate());
  const [schoolDateManuallySet, setSchoolDateManuallySet] = useState(false);

  // Update school date when time changes (for the smart default)
  useEffect(() => {
    if (viewMode === 'school' && !schoolDateManuallySet) {
      setSchoolDate(getSmartSchoolDate());
    }
  }, [viewMode, schoolDateManuallySet, getSmartSchoolDate]);

  // School date navigation label
  const schoolDateLabel = useMemo(() => {
    const nowTz = getNowInTz(familyTimezone);
    const today = startOfDay(nowTz);

    if (isSameDay(schoolDate, today)) {
      return 'Today';
    }
    if (isTomorrow(schoolDate)) {
      return 'Tomorrow';
    }
    // Show the day name (e.g. "Monday")
    return format(schoolDate, 'EEEE');
  }, [schoolDate, familyTimezone]);

  const schoolDateSubtitle = useMemo(() => {
    return format(schoolDate, 'EEE d MMM');
  }, [schoolDate]);

  const handleSchoolPrev = useCallback(() => {
    setSchoolDate((prev) => getPrevSchoolDay(prev));
    setSchoolDateManuallySet(true);
  }, []);

  const handleSchoolNext = useCallback(() => {
    setSchoolDate((prev) => getNextSchoolDay(prev));
    setSchoolDateManuallySet(true);
  }, []);

  const handleSchoolReset = useCallback(() => {
    setSchoolDate(getSmartSchoolDate());
    setSchoolDateManuallySet(false);
  }, [getSmartSchoolDate]);

  return {
    schoolDate,
    schoolDateLabel,
    schoolDateSubtitle,
    handleSchoolPrev,
    handleSchoolNext,
    handleSchoolReset,
  };
}
